import moment from 'moment';
import { Event, EventType, StaffType } from '../services/Types';
import { EventStatus } from '../services/Types';
import { formatDate, convertDbTimeIntoString } from '../services/Helper';
import { useAllEvents } from './useAllEvents';
import { getStatusLabel } from '../helpers/statusColors';

type ColumnKey = {
  key: keyof Event;
  label: string;
  formatter: (row: Event, key: keyof Event) => string;
};

/** Escape CSV value: wrap in quotes and escape internal quotes */
const csvEscape = (val: string | number): string => {
  const s = String(val ?? '');
  return `"${s.replace(/"/g, '""')}"`;
};

// SendInvitation mapping: OWN->OWN, ALL->BOTH, NOTOWN->WFGPS
const SEND_INVITATION_MAP: Record<string, string> = {
  OWN: 'OWN',
  ALL: 'BOTH',
  NOTOWN: 'WFGPS',
};

/** Format room booking date+time as MM/DD/YYYY hh:mm AM/PM */
const formatBookingDateTime = (booking: { date: any; startTime?: number }): string => {
  const dateStr = formatDate(booking.date, 'date') as string;
  const timeStr = booking.startTime != null ? convertDbTimeIntoString(booking.startTime) : '';
  return timeStr ? `${dateStr} ${timeStr}` : dateStr;
};

// Exportable formatters for Event data
export const eventFormatter = (getSeriesTitle: (seriesId: number) => string) => {
  const errorString = '"Error parsing"';

  return {
    default: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (value === null) return '""';
      return typeof value === 'string' ? csvEscape(value) : csvEscape(String(value));
    },
    status: (row: Event, _key: keyof Event) => {
      const value = row.status;
      if (value == null) return '""';
      return csvEscape(getStatusLabel(String(value)));
    },
    url: (row: Event, key: keyof Event) => {
      const value = row[key];
      const base = process.env.REACT_APP_WEBSITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://etaevents.safalapps.com');
      const path = `/event/review/${value}`;
      const fullUrl = base?.startsWith('http') ? base + path : `https://etaevents.safalapps.com${path}`;
      return csvEscape(fullUrl);
    },
    list: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (value === null) return '""';
      if (typeof value === 'string') {
        try {
          const parsedArray = JSON.parse(value);
          if (Array.isArray(parsedArray)) {
            return csvEscape(parsedArray.join('\n'));
          }
        } catch (e) {
          return csvEscape(value.replace(/,/g, '|'));
        }
      }
      return csvEscape(String(value));
    },
    seriesDetails: (row: Event, _key: keyof Event) => {
      if (!row.seriesId || row.seriesId <= 0) return '""';
      return row.eventType === EventType.SERIES ? '"Series Parent"' : '"Series Child"';
    },
    seriesTitle: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (!value || Number(value) <= 0) return '""';
      return csvEscape(getSeriesTitle(value as number));
    },
    person: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (value && typeof value === 'object' && 'firstName' in value && 'lastName' in value) {
        return csvEscape(`${value.firstName} ${value.lastName}`);
      }
      return '""';
    },
    personEmail: (row: Event, key: keyof Event) => {
      const value = row[key] as { email?: string } | undefined;
      return value?.email ? csvEscape(value.email) : '""';
    },
    programOffice: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (value && typeof value === 'object' && 'title' in value) {
        return csvEscape(value.title);
      }
      return '""';
    },
    staff: (staff_type: StaffType) => (row: Event, _key: keyof Event) => {
      const value = row.staff;
      if (Array.isArray(value)) {
        const staffLines = value
          .filter((staff: any) => typeof staff === 'object' && staff.staff_type === staff_type)
          .map((staff: any) => `${staff.name || ''} | ${staff.jobTitle || ''} | ${staff.programOffice || ''} | ${staff.phone || ''}`)
          .filter(Boolean);
        return csvEscape(staffLines.join('\n'));
      }
      if (staff_type === StaffType.PRESENTERS && row.tbd_presenters === 1) return '"TBD"';
      if (staff_type === StaffType.MODERATORS && row.tbd_moderators === 1) return '"TBD"';
      if (staff_type === StaffType.SUPPORT_STAFF && row.tbd_supportstaff === 1) return '"TBD"';
      return csvEscape(errorString);
    },
    staffCount: (staff_type: StaffType) => (row: Event, _key: keyof Event) => {
      const value = row.staff;
      if (Array.isArray(value)) {
        const count = value.filter((s: any) => s?.staff_type === staff_type).length;
        return String(count);
      }
      if (staff_type === StaffType.PRESENTERS && row.tbd_presenters === 1) return '1';
      if (staff_type === StaffType.MODERATORS && row.tbd_moderators === 1) return '1';
      if (staff_type === StaffType.SUPPORT_STAFF && row.tbd_supportstaff === 1) return '1';
      return '0';
    },
    roomBooking: (row: Event, key: keyof Event) => {
      const value = row[key];
      if (Array.isArray(value)) {
        const bookings = value
          .map((booking: any) =>
            typeof booking === 'object' && booking && 'date' in booking && 'startTime' in booking && 'endTime' in booking
              ? `${booking.category}: ${formatDate(booking.date, 'date') as string} ${convertDbTimeIntoString(booking.startTime)}-${convertDbTimeIntoString(booking.endTime)}`
              : ''
          )
          .filter(Boolean);
        return csvEscape(bookings.join('\n'));
      }
      return '""';
    },
    eventCloseout: (row: Event, key: keyof Event) => {
      const value = row[key];
      let fileCount = '0';
      if (value && typeof value === 'object' && 'files' in value) {
        fileCount = Array.isArray(value.files) ? value.files.length.toString() : errorString;
      }
      if (value && typeof value === 'object' && 'highlightsActionItems' in value) {
        return csvEscape(`Registrants: ${value.numberOfRegistrants} | Attendees: ${value.numberOfAttendees} | Ratings: ${value.participantRatingsSummary} | Action Items: ${value.highlightsActionItems} | File count: ${fileCount}`);
      }
      return '""';
    },
    /** Latest approver from history for level 1, 2, or 3. Blank if none. */
    approverFromHistory: (level: 1 | 2 | 3) => (row: Event, _key: keyof Event) => {
      const key = `approver${level}` as 'approver1' | 'approver2' | 'approver3';
      const name = row.latestApprovers?.[key];
      return name ? csvEscape(name) : '""';
    },
    boolean: (row: Event, key: keyof Event) => {
      const value = row[key];
      return value ? '"Yes"' : '"No"';
    },
    date: (row: Event, key: keyof Event) => {
      const value = row[key];
      return value ? csvEscape(formatDate(value as string, 'date') as string) : '""';
    },
    time: (row: Event, key: keyof Event) => {
      const value = row[key];
      return value ? csvEscape(convertDbTimeIntoString(value as number)) : '""';
    },
    number: (row: Event, key: keyof Event) => String(row[key] ?? 0),
    sendInvitation: (row: Event, _key: keyof Event) => {
      const v = row.sendInvitation;
      return csvEscape(SEND_INVITATION_MAP[v] ?? v ?? '');
    },
    /** Webinar: Dry Run Date and Time (MM/DD/YYYY hh:mm AM/PM) */
    dryRunDateTime: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBINAR) return '""';
      const rb = row.roomBooking;
      if (!Array.isArray(rb)) return '""';
      const dry = rb.find((b: any) => b?.category === 'dry');
      if (!dry) return '""';
      return csvEscape(formatBookingDateTime(dry));
    },
    /** Webinar: Live Date and Time */
    liveDateTime: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBINAR) return '""';
      const rb = row.roomBooking;
      if (!Array.isArray(rb)) return '""';
      const live = rb.find((b: any) => b?.category === 'live');
      if (!live) return '""';
      return csvEscape(formatBookingDateTime(live));
    },
    /** Webcast: Recording Date and Time */
    recordingDateTime: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBCAST) return '""';
      const rb = row.roomBooking;
      if (!Array.isArray(rb)) return '""';
      const live = rb.find((b: any) => b?.category === 'live');
      if (!live) return '""';
      return csvEscape(formatBookingDateTime(live));
    },
    /** Webcast: Publish Date and Time (date-only; publicationDate is the source) */
    publishDateTime: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBCAST) return '""';
      const rb = row.roomBooking;
      if (Array.isArray(rb)) {
        const dry = rb.find((b: any) => b?.category === 'dry');
        if (dry) return csvEscape(formatBookingDateTime(dry));
      }
      if (row.publicationDate) return csvEscape(formatDate(row.publicationDate, 'date') as string);
      return '""';
    },
    /** Webinar: Event Features (Features Transcription) */
    eventFeatures: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBINAR) return '""';
      return row.featuresTranscription ? csvEscape(row.featuresTranscription) : '""';
    },
    /** Webinar: Special Features (Features Script) */
    specialFeatures: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBINAR) return '""';
      return row.featuresScript ? csvEscape(row.featuresScript) : '""';
    },
    /** Webcast: Transcript Requested (Features Transcription) */
    transcriptRequested: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBCAST) return '""';
      return row.featuresTranscription ? csvEscape(row.featuresTranscription) : '""';
    },
    /** Webcast: Own Script (Features Script) */
    ownScript: (row: Event, _key: keyof Event) => {
      if (row.eventType !== EventType.WEBCAST) return '""';
      return row.featuresScript ? csvEscape(row.featuresScript) : '""';
    },
  };
};

export type ExportOptions = {
  searchParams?: URLSearchParams;
};

export const useExportEvents = () => {
  const { getSeriesTitle } = useAllEvents();

  const exportEventData = async (selectedEvents: Event[], options?: ExportOptions) => {
    const filtered = selectedEvents.filter((e) => e.status !== EventStatus.BLOCKED);

    const format = eventFormatter(getSeriesTitle);
    const fn = (formatterFn: (row: Event, key: keyof Event) => string) =>
      (row: Event, key: keyof Event) => formatterFn(row, key);

    const columnKeys: ColumnKey[] = [
      { key: 'id', label: 'ID', formatter: fn(format.default) },
      { key: 'seriesId', label: 'Series Details', formatter: fn(format.seriesDetails) },
      { key: 'seriesId', label: 'Series Title', formatter: fn(format.seriesTitle) },
      { key: 'creater', label: 'Requestor', formatter: fn(format.person) },
      { key: 'creater', label: 'Requester email', formatter: fn(format.personEmail) },
      { key: 'programOffice', label: 'Program Office', formatter: fn(format.programOffice) },
      { key: 'id', label: 'Program Office Approver', formatter: fn(format.approverFromHistory(1)) },
      { key: 'id', label: 'Approver2', formatter: fn(format.approverFromHistory(2)) },
      { key: 'id', label: 'Approver3', formatter: fn(format.approverFromHistory(3)) },
      { key: 'status', label: 'Status', formatter: fn(format.status) },
      { key: 'eventType', label: 'Event Type', formatter: fn(format.default) },
      { key: 'title', label: 'Title', formatter: fn(format.default) },
      { key: 'summary', label: 'Summary', formatter: fn(format.default) },
      { key: 'description', label: 'Description', formatter: fn(format.default) },
      { key: 'roomBooking', label: 'Dry Run Date and Time', formatter: fn(format.dryRunDateTime) },
      { key: 'roomBooking', label: 'Live Date and Time', formatter: fn(format.liveDateTime) },
      { key: 'roomBooking', label: 'Recording Date and Time', formatter: fn(format.recordingDateTime) },
      { key: 'roomBooking', label: 'Publish Date and Time', formatter: fn(format.publishDateTime) },
      { key: 'id', label: 'URL', formatter: fn(format.url) },
      { key: 'staff', label: 'Presenter Count', formatter: fn(format.staffCount(StaffType.PRESENTERS)) },
      { key: 'staff', label: 'Presenters', formatter: fn(format.staff(StaffType.PRESENTERS)) },
      { key: 'staff', label: 'Moderator Count', formatter: fn(format.staffCount(StaffType.MODERATORS)) },
      { key: 'staff', label: 'Moderators', formatter: fn(format.staff(StaffType.MODERATORS)) },
      { key: 'staff', label: 'Support Staff Count', formatter: fn(format.staffCount(StaffType.SUPPORT_STAFF)) },
      { key: 'staff', label: 'Support Staff', formatter: fn(format.staff(StaffType.SUPPORT_STAFF)) },
      { key: 'overHundredParticipants', label: 'Over 300 Participants', formatter: fn(format.boolean) },
      { key: 'sendInvitation', label: 'Send Invitation', formatter: fn(format.sendInvitation) },
      { key: 'invitationDistributionList', label: 'Invitation Distribution List', formatter: fn(format.list) },
      { key: 'invitationNotes', label: 'Audience & Invitation Requests', formatter: fn(format.default) },
      { key: 'publicOrPrivate', label: 'Public or Private', formatter: fn(format.default) },
      { key: 'logisticNeed', label: 'Virtual Features', formatter: fn(format.list) },
      { key: 'supportNeeded', label: 'Special Requests', formatter: fn(format.default) },
      { key: 'additionalRequest', label: 'Additional Request', formatter: fn(format.default) },
      { key: 'featuresTranscription', label: 'Event Features', formatter: fn(format.eventFeatures) },
      { key: 'featuresScript', label: 'Special Features', formatter: fn(format.specialFeatures) },
      { key: 'featuresTranscription', label: 'Transcript Requested', formatter: fn(format.transcriptRequested) },
      { key: 'featuresScript', label: 'Own Script', formatter: fn(format.ownScript) },
      { key: 'eventCloseout', label: 'Event Closeout', formatter: fn(format.eventCloseout) },
    ];

    const header = columnKeys.map((k) => `"${k.label}"`).join(',');
    const rows = filtered.map((row) =>
      columnKeys.map((k) => k.formatter(row, k.key as keyof Event)).join(',')
    );

    const searchUrl = options?.searchParams ? buildSearchUrl(options.searchParams) : null;
    const csvLines = searchUrl ? [searchUrl, '', header, ...rows] : [header, ...rows];
    const csv = csvLines.join('\n');

    const filename = `${moment().format('YYYY-MM-DD')}_event_planner_export.csv`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return { exportEventData };
};

function buildSearchUrl(params: URLSearchParams): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://etaevents.safalapps.com';
  const path = typeof window !== 'undefined' ? window.location.pathname : '/admin/events';
  const qs = params.toString();
  return qs ? `${base}${path}?${qs}` : `${base}${path}`;
}
